import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "@radix-ui/react-slot";
import { Spinner } from "@/components/ui/loading";
import { cn } from "@/lib/cn";

// Only the `default` variant ever touches the accent. Everything else is
// tone-neutral and reads correctly against either theme, because all five
// variants resolve through semantic tokens rather than literal colours.
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-accent text-fg-on-accent hover:bg-accent/90",
        secondary:
          "bg-bg-elevated text-fg border border-border hover:bg-bg-sunken",
        destructive:
          "border border-danger/40 bg-danger/10 text-danger hover:border-danger",
        ghost: "text-fg hover:bg-bg-elevated",
        outline:
          "border border-border bg-transparent text-fg hover:bg-bg-elevated",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-8 px-3 text-xs",
        lg: "h-11 px-6 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    /**
     * Render the child element instead of a `<button>`, keeping these styles.
     *
     * This is how a router `<Link>` gets button styling without nesting an
     * anchor inside a button — which is invalid HTML and breaks keyboard
     * activation in ways that only show up in an accessibility audit.
     */
    asChild?: boolean;

    /**
     * Show a spinner and refuse activation while an action is in flight.
     *
     * Disabling alone — which is what this codebase did before — dims the
     * control and says nothing, so a slow request looks like a click that
     * did not register and the user clicks again. The spinner is prepended
     * rather than replacing the label, so the button keeps its meaning while
     * it works.
     *
     * Ignored under `asChild`: the child is not a `<button>`, so there is
     * nothing to disable and a link cannot be in flight.
     */
    loading?: boolean;
  };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    if (asChild) {
      return (
        <Slot
          ref={ref}
          className={cn(buttonVariants({ variant, size, className }))}
          {...props}
        >
          {children}
        </Slot>
      );
    }
    return (
      <button
        ref={ref}
        // aria-busy reports the pending state; disabled stops a second click
        // queueing a second mutation. An explicit `disabled` still wins, so a
        // button that is disabled for its own reasons stays that way.
        aria-busy={loading || undefined}
        disabled={disabled || loading}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      >
        {loading && <Spinner size="sm" />}
        {children}
      </button>
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
